import type { ApiResponse } from "@/types";
import { legacyHttpClient } from "@services/http/legacyHttpClient";

/** Request payload for updating client profile. */
export interface UpdateProfileRequest {
  email: string;
  fullname: string;
  surname: string;
  address: string;
  phone: string;
  uuid?: string;
}

/** Backend response from update_client endpoint. */
export interface UpdateClientResponse {
  id: number;
  address: string;
  uuid: string;
  email: string;
  fullname: string;
  surname: string;
  tipoDocumento: string;
  phone: string;
  updateAt: string;
  state: number;
}

/**
 * Profile update service.
 * Handles user profile updates via the legacy backend update_client endpoint.
 */
export const profileService = {
  /**
   * Update user profile in the backend.
   * Calls /api/web/update_client endpoint.
   *
   * @param email - User email (required for lookup)
   * @param fullname - Full name to update
   * @param address - Address to update
   * @param phone - Phone number to update
   * @returns Updated client data from backend
   */
  async updateProfile(
    email: string,
    fullname: string,
    address: string,
    phone: string,
    cedula?: string,
  ): Promise<ApiResponse<UpdateClientResponse>> {
    try {
      // Backend uses fullname (full name) + surname (apellido) as separate fields.
      // surname = last word(s), fullname = everything before surname.
      const parts = fullname.trim().split(" ");
      const surname = parts.length > 1 ? parts.slice(1).join(" ") : parts[0] ?? fullname;

      const payload: UpdateProfileRequest = {
        email,
        fullname,
        surname,
        address,
        phone,
        ...(cedula ? { uuid: cedula } : {}),
      };

      const response = await legacyHttpClient.post<UpdateClientResponse>(
        "/api/web/update_client",
        payload,
      );

      console.log("[profileService] update_client response:", response);

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        data: {} as UpdateClientResponse,
        message:
          error instanceof Error
            ? error.message
            : "Error al actualizar el perfil",
      };
    }
  },
};
